import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { Webhook, RefreshCw, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WebhookMonitor = () => {
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTenant, setFilterTenant] = useState('');

  useEffect(() => {
    loadWebhooks();
    const interval = setInterval(loadWebhooks, 10000);
    return () => clearInterval(interval);
  }, [filterStatus, filterTenant]);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterTenant) params.append('tenant_id', filterTenant);
      
      const response = await axios.get(`${API_URL}/api/super-admin/webhooks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      console.error('Load webhooks error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (webhookId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/super-admin/webhooks/${webhookId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Webhook queued for retry');
      loadWebhooks();
    } catch (error) {
      console.error('Retry error:', error);
      toast.error('Failed to retry webhook');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: 'secondary', icon: Clock, color: 'text-yellow-600' },
      sent: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      failed: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      dlq: { variant: 'destructive', icon: AlertCircle, color: 'text-orange-600' }
    };
    return variants[status] || variants.pending;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Webhook className="h-8 w-8" />
            Webhook Monitor
          </h1>
          <p className="text-gray-500 mt-1">{webhooks.length} webhooks</p>
        </div>
        <Button onClick={loadWebhooks} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full border rounded-md px-3 py-2 mt-1"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="dlq">Dead Letter Queue</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Tenant ID</label>
              <Input
                value={filterTenant}
                onChange={(e) => setFilterTenant(e.target.value)}
                placeholder="Filter by tenant..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : webhooks.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500">No webhooks found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {webhooks.map((webhook) => {
            const statusInfo = getStatusBadge(webhook.status);
            const StatusIcon = statusInfo.icon;
            return (
              <Card key={webhook.webhook_id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-3">
                        <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                        <span className="font-mono text-sm">{webhook.webhook_id}</span>
                        <Badge {...statusInfo}>{webhook.status.toUpperCase()}</Badge>
                        {webhook.status === 'failed' && (
                          <Badge variant="outline">Retry {webhook.retry_count}/{webhook.max_retries}</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Event:</span>
                          <span className="ml-2 font-medium">{webhook.event_type}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Delivery:</span>
                          <span className="ml-2 font-mono text-xs">{webhook.delivery_id}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Tenant:</span>
                          <span className="ml-2 font-mono text-xs">{webhook.tenant_id}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Created:</span>
                          <span className="ml-2">{new Date(webhook.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      {webhook.last_error && (
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                          <strong>Error:</strong> {webhook.last_error}
                        </div>
                      )}
                    </div>
                    {(webhook.status === 'failed' || webhook.status === 'dlq') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetry(webhook.webhook_id)}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Retry
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WebhookMonitor;
